// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import SvgIconStyle from '../../../components/SvgIconStyle';
import { MerchantProgramme, MerchantProgramType } from 'src/@types/merchant';
import { lowerCase, readableStringFormatter } from 'src/utils/stringFormatter';
import { NavListProps } from 'src/components/nav-section';
import {
  FileNavIcon,
  ProfileNavIcon,
  GlobNavIcon,
  CheckIcon,
  PaymentIcon,
  SettingsIcon,
  CO2Icon,
  CreditsIcon,
  TextIcon,
  CustomIcon,
  GlobeIcon,
  ConfigurationIcon,
  FlightIcon,
} from 'src/theme/overrides/CustomIcons';

// ----------------------------------------------------------------------

const getIcon = (name: string) => (
  <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  dashboard: getIcon('ic_dashboard'),
  toolkit: getIcon('ic_toolkit'),
  impactTools: getIcon('ic_impact_tools'),
  credits: getIcon('ic_credits'),
  myCredits: getIcon('ic_my_credits'),
  businessCalculator: getIcon('ic_business_calculator'),
  calculators: getIcon('ic_calculators'),
  impactPages: getIcon('ic_impact_pages'),
  impactStories: getIcon('ic_impact_stories'),
  settings: getIcon('ic_menu_settings'),
  emissions: getIcon('ic_co2'),
  programs: getIcon('ic_people'),
  account: getIcon('ic_settings'),
  impact: getIcon('ic_settings'),
  event: getIcon('ic_event'),
  eventProgramme: getIcon('ic_event_programme'),
  businessProgramme: getIcon('ic_business_programme'),
  employeeProgramme: getIcon('ic_employee_programme'),
  flightProgramme: getIcon('ic_flight_programme'),
  customerProgramme: getIcon('ic_customer_programme'),
  journeyProgramme: getIcon('ic_journey_programme'),
};

const generateRouteChildren = (el: any) => {
  const repeatableEachProgrammeLinks = [
    {
      title: 'Overview',
      path: PATH_DASHBOARD.programs.engagement(lowerCase(el.type), el.code),
      id: 'nav-item-engagement',
      icon: <CheckIcon />
    },
    {
      title: 'Contributions',
      path: PATH_DASHBOARD.programs.transactions(lowerCase(el.type), el.code),
      id: 'nav-item-collective-credits',
      icon: <FileNavIcon />
    },
    {
      title: 'Impact',
      path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code),
      id: 'nav-item-impact',
      icon: <GlobNavIcon />
    },
  ];

  const children: { [key: string]: NavListProps[] } = {
    CORPORATE_FLIGHT: [
      {
        title: 'Overview',
        path: PATH_DASHBOARD.programs.engagement(lowerCase(el.type), el.code),
        id: 'nav-item-light-corporate-engagement',
        icon: <CheckIcon />
      },
      {
        title: 'Emissions',
        path: PATH_DASHBOARD.programs.emissions(lowerCase(el.type), el.code),
        hideDatePicker: true,
        id: 'nav-item-flight-corporate-emissions',
        icon: <CO2Icon />
      },
      {
        title: 'Contributions',
        path: PATH_DASHBOARD.programs.transactions(lowerCase(el.type), el.code),
        id: 'nav-item-flight-corporate-contributions',
        icon: <FileNavIcon />
      },
      {
        title: 'Impact',
        path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code),
        id: 'nav-item-flight-corporate-impact',
        icon: <GlobNavIcon />
      },
      {
        title: 'Flights',
        path: PATH_DASHBOARD.programs.flights(lowerCase(el.type), el.code),
        id: 'nav-item-flights',
        icon: <FlightIcon />
      },
      {
        title: 'Emission record',
        path: PATH_DASHBOARD.programs.emissionRecord(lowerCase(el.type), el.code, '[a-zA-Z0-9-]*'),
        id: 'nav-item-flight-corporate-emission-record',
        hide: true,
        hideDatePicker: true,
        isDynamicPath: true
      },
      {
        title: 'Add estimate',
        path: PATH_DASHBOARD.programs.addEmission(lowerCase(el.type), el.code, '[a-zA-Z0-9-]*'),
        id: 'nav-item-flight-corporate-add-estimate',
        hide: true,
        hideDatePicker: true,
        isDynamicPath: true
      },
      {
        title: 'Calculate',
        path: PATH_DASHBOARD.programs.calculate(lowerCase(el.type), el.code),
        id: 'nav-item-flight-corporate-calculate',
        hide: true,
        hideDatePicker: true,
      },
      // {
      //   title: 'Travellers',
      //   path: PATH_DASHBOARD.programs.customers(lowerCase(el.type), el.code),
      //   id: 'nav-item-customers',
      //   icon: <ProfileNavIcon />
      // },
    ],
    FLIGHT: [
      // repeatableEachProgrammeLinks - sharable items across every menu programme
      ...repeatableEachProgrammeLinks,
      {
        title: 'Travellers',
        path: PATH_DASHBOARD.programs.customers(lowerCase(el.type), el.code),
        id: 'nav-item-customers',
        icon: <ProfileNavIcon />
      },
    ],
    JOURNEY: [
      ...repeatableEachProgrammeLinks,
      {
        title: 'Customers',
        path: PATH_DASHBOARD.programs.customers(lowerCase(el.type), el.code),
        id: 'nav-item-customers',
        icon: <ProfileNavIcon />
      },
    ],
    EMPLOYEE: [
      ...repeatableEachProgrammeLinks,
      {
        title: 'Employees',
        path: PATH_DASHBOARD.programs.employees(lowerCase(el.type), el.code),
        id: 'nav-item-employees',
        icon: <ProfileNavIcon />
      },
      {
        title: 'Configuration',
        path: PATH_DASHBOARD.programs.configuration(lowerCase(el.type), el.code),
        id: 'nav-item-configuration',
        icon: <ConfigurationIcon />
      },
    ],
  }

  return children[el.type];
}

export interface ProgrammeMenu {
  title: string;
  code: string;
  path: string;
  id: string;
  icon: JSX.Element;
  programme: keyof typeof MerchantProgramType;
  hideDatePicker?: boolean;
  children: NavListProps[];
}

const getProgrammeIcon = (programmeType: keyof typeof MerchantProgramType) => {
  switch(programmeType) {
    case MerchantProgramType.EMPLOYEE:
      return ICONS.employeeProgramme;
    case MerchantProgramType.FLIGHT:
    case MerchantProgramType.CORPORATE_FLIGHT:
      return ICONS.flightProgramme;
    case MerchantProgramType.JOURNEY:
      return ICONS.journeyProgramme;
    default:
      return ICONS.credits;
  }
}

export const getProgramMenu = (programmes: MerchantProgramme[]) => {
  const menu: ProgrammeMenu[] = programmes?.map((el) => {
    const children = generateRouteChildren(el);
    const title = el.name || el.type;
    return {
      title: readableStringFormatter(title),
      code: el.code,
      path: PATH_DASHBOARD.programs.program(lowerCase(el.type), el.code),
      id: 'nav-item-merchant-programme',
      icon: getProgrammeIcon(el.type),
      programme: el.type,
      children: children,
    }
  });

  menu.unshift(
{
        title: 'Event',
        code: 'EVENT',
        id: 'nav-item-event',
        path: PATH_DASHBOARD.programs.event.root,
        icon: ICONS.eventProgramme,
        programme: 'EVENT',
        children: [
          {
            title: 'Overview',
            path: PATH_DASHBOARD.programs.event.overview,
            hideDatePicker: true,
            id: 'nav-item-event-engagement',
            icon: <CheckIcon />
          },
          {
            title: 'Impact',
            path: PATH_DASHBOARD.programs.eventImpact,
            id: 'nav-item-impact',
            icon: <GlobNavIcon />
          },
          {
            title: 'Emissions',
            path: PATH_DASHBOARD.programs.event.emissions,
            hideDatePicker: true,
            id: 'nav-item-event-emissions',
            icon: <CO2Icon />
          },
          {
            title: 'Amount',
            path: PATH_DASHBOARD.programs.event.amount,
            id: 'nav-item-event-emissions-amount',
            hideDatePicker: true,
            hide: true,
          },
          {
            title: 'Calculate',
            path: PATH_DASHBOARD.programs.event.calculate,
            id: 'nav-item-event-emissions-calculate',
            hideDatePicker: true,
            hide: true,
          },
          {
            title: 'Edit',
            path: PATH_DASHBOARD.programs.event.edit('[a-zA-Z0-9-]*'),
            id: 'nav-item-event-emissions-edit',
            isDynamicPath: true,
            hideDatePicker: true,
            hide: true,
          },
          {
            title: 'Edit',
            path: PATH_DASHBOARD.programs.event.amountEdit('[a-zA-Z0-9-]*'),
            id: 'nav-item-event-emissions-amount-edit',
            isDynamicPath: true,
            hideDatePicker: true,
            hide: true,
          },
          {
            title: 'View',
            path: PATH_DASHBOARD.programs.event.view('[a-zA-Z0-9-]*$'),
            id: 'nav-item-event-emissions-view',
            isDynamicPath: true,
            hideDatePicker: true,
            hide: true,
          },
          {
            title: 'View',
            path: PATH_DASHBOARD.programs.event.amountView('[a-zA-Z0-9-]*$'),
            id: 'nav-item-event-emissions-amount-view',
            isDynamicPath: true,
            hideDatePicker: true,
            hide: true,
          }
        ]
      }
  );

  menu.unshift(
      {
        title: 'Business',
        code: 'BUSINESS',
        id: 'nav-item-business',
        path: PATH_DASHBOARD.programs.business.root,
        icon: ICONS.businessProgramme,
        programme: 'BUSINESS',
        children: [
          {
            title: 'Emissions',
            path: PATH_DASHBOARD.programs.business.emissions,
            hideDatePicker: true,
            id: 'nav-item-business-emissions',
            icon: <CO2Icon />
          },
          {
            title: 'Contributions',
            path: PATH_DASHBOARD.programs.business.contributions,
            id: 'nav-item-business-contributions',
            icon: <FileNavIcon />
          },
          {
            title: 'Amount',
            path: PATH_DASHBOARD.programs.business.amount,
            id: 'nav-item-business-emissions-amount',
            hideDatePicker: true,
            hide: true
          },
          {
            title: 'Calculate',
            path: PATH_DASHBOARD.programs.business.calculate,
            id: 'nav-item-business-emissions-calculate',
            hideDatePicker: true,
            hide: true
          },
          {
            title: 'Edit',
            path: PATH_DASHBOARD.programs.business.edit('[a-zA-Z0-9-]*'),
            id: 'nav-item-business-emissions-edit',
            isDynamicPath: true,
            hideDatePicker: true,
            hide: true,
          },
          {
            title: 'Edit',
            path: PATH_DASHBOARD.programs.business.amountEdit('[a-zA-Z0-9-]*'),
            id: 'nav-item-business-emissions-amount-edit',
            isDynamicPath: true,
            hideDatePicker: true,
            hide: true,
          },
          {
            title: 'View',
            path: PATH_DASHBOARD.programs.business.view('[a-zA-Z0-9-]*$'),
            id: 'nav-item-business-emissions-view',
            isDynamicPath: true,
            hideDatePicker: true,
            hide: true,
          },
          {
            title: 'View',
            path: PATH_DASHBOARD.programs.business.amountView('[a-zA-Z0-9-]*$'),
            id: 'nav-item-business-emissions-amount-view',
            isDynamicPath: true,
            hideDatePicker: true,
            hide: true,
          }
        ]
      }
  );

  if(!programmes || !programmes.length) {

    menu.unshift(
        {
          title: 'Customer',
          code: 'CUSTOMER',
          id: 'nav-item-business',
          path: PATH_DASHBOARD.programs.customer.root,
          icon: ICONS.customerProgramme,
          programme: 'BUSINESS',
          children: [
            {
              title: 'Contributions',
              path: PATH_DASHBOARD.programs.customer.contributions,
              id: 'nav-item-customer-contributions',
              icon: ICONS.credits
            },
          ]
        }
    );
  }

  return menu;
};

export const TLSettingsMenuItems = [
  {
    title: 'Metadata',
    path: '#generalSettings', // equals to section id
    icon: <SettingsIcon />,
  },
  {
    title: 'Header',
    path: '#headerSection',
    icon: <TextIcon />,
  },
  {
    title: 'Intro',
    path: '#introSection',
    icon: <GlobeIcon />,
  },
  {
    title: 'Impact',
    path: '#impactSection',
    icon: <CO2Icon />,
  },
  {
    title: 'Explainer',
    path: '#howItWorksSection',
    icon: <CreditsIcon />,
  },
  {
    title: 'Custom',
    path: '#customSection',
    icon: <CustomIcon />,
  },
];

export const merchantStatsMenu = [
  {
    title: 'Totals',
    code: 'TOTALS',
    path: PATH_DASHBOARD.impactStories.insights.root,
    id: 'nav-item-insights',
    icon: ICONS.dashboard,
    children: [
      {
        title: 'Impact',
        path: PATH_DASHBOARD.impactStories.insights.impact,
        id: 'nav-item-insights-impact',
        icon: <GlobNavIcon />
      },
      {
        title: 'Projects',
        path: PATH_DASHBOARD.impactStories.insights.projects,
        id: 'nav-item-insights-projects',
        icon: <PaymentIcon sx={{ '& > *': { fill: '#74635B' } }} />
      },
      {
        title: 'Contributions',
        path: PATH_DASHBOARD.impactStories.insights.transactions,
        id: 'nav-item-insights-transactions',
        icon: <FileNavIcon />
      },
    ],
  },
];

const navConfig = [

  {
    items: [
      {
        title: 'Dashboard',
        code: 'DASHBOARD',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        id: 'nav-item-dashboard',
      },
    ],
  },

  {
    items: merchantStatsMenu,
  },

  {
    title: 'PROGRAMMES',
    id: 'nav-item-group-programmes',
    items: [
      {
        title: 'Toolkit',
        code: 'TOOLKIT',
        path: PATH_DASHBOARD.programs.toolkit,
        id: 'nav-item-toolkit',
        icon: ICONS.toolkit
      },
    ],
  },

  {
    title: 'IMPACT STORIES',
    id: 'nav-item-group-impact-stories',
    items: [
      {
        title: 'Landing pages',
        code: 'LANDING_PAGES',
        path: PATH_DASHBOARD.impactStories.impactPages,
        id: 'nav-item-impact-pages',
        icon: ICONS.impactPages
      },
      {
        title: 'Impact tools',
        code: 'IMPACT_TOOLS',
        path: PATH_DASHBOARD.impactStories.tools,
        id: 'nav-item-impact-stories-toolkit',
        icon: ICONS.impactTools
      },
    ],
  },

  {
    title: 'CONFIGURATION',
    id: 'nav-item-group-configuration',
    items: [
      {
        title: 'Settings',
        code: 'SETTINGS',
        path: PATH_DASHBOARD.settings.root,
        id: 'nav-item-settings',
        icon: ICONS.settings,
        children: [
          {
            title: 'Theme',
            path: PATH_DASHBOARD.settings.theme,
            id: 'nav-item-theme'
          },
          {
            title: 'Business',
            path: PATH_DASHBOARD.settings.business,
          },
          {
            title: 'Default basket',
            path: PATH_DASHBOARD.settings.defaultBasket,
          },
          {
            title: 'Billing',
            path: PATH_DASHBOARD.settings.billing,
          },
          {
            title: 'Menu',
            path: PATH_DASHBOARD.settings.menu,
          },
          {
            title: 'Receipt emails',
            path: PATH_DASHBOARD.settings.emails,
          },
          {
            title: 'Dashboard',
            path: PATH_DASHBOARD.settings.dashboard,
          },
        ],
      }
    ]
  },
];

export default navConfig;
